import { APlugin } from "./a-plugin";
import { EAddressField } from "../enum/e-address-field";
import { AddressAutocompletionRepo } from "../../../utils/typescript/repositories/address-autocompletion.repo";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  Observable,
  switchMap,
  take,
  tap,
} from "rxjs";
import { AddressAutocompletionResponseModel } from "../../../utils/typescript/model/address-autocompletion/address-autocompletion-response.model";
import { AddressAutocompletionFeatureResultModel } from "../../../utils/typescript/model/address-autocompletion/address-autocompletion-feature-result.model";
import { DomService } from "../../../utils/typescript/service/dom.service";
import { AddressAutocompletionEventConfig } from "./config/address-autocompletion-event.config";

export class AddressAutocompletionPlugin extends APlugin {
  readonly formElement: HTMLFormElement;
  protected elements: Map<EAddressField, unknown> = new Map();
  protected documentClickEvent$: Observable<Event>;

  // We need the placeId from the api response to filter the street suggestions by city
  protected placeId: string = "";

  constructor(formElement: HTMLFormElement) {
    super();

    this.formElement = formElement;
    this.buildElementList();
    this.addEvents();
  }

  protected addEvents() {
    if (
      !this.elements.get(EAddressField.MAIN_STREET) ||
      !this.elements.get(EAddressField.MAIN_POSTAL_CODE) ||
      !this.elements.get(EAddressField.MAIN_CITY)
    ) {
      return console.warn(
        "address-suggestion-validator.ts: Address autocompletion disabled, because one or more necessary fields could not be found.",
      );
    }

    this.documentClickEvent$ = fromEvent(document, "click");

    // simplify?
    fromEvent(
      <HTMLDivElement>this.elements.get(EAddressField.MAIN_CLOSE_BUTTON),
      "click",
    ).subscribe(() => {
      (
        this.elements.get(EAddressField.MAIN_SUGGESTIONS) as HTMLDivElement
      ).classList.add("hidden");
      this.setInputAutocompleteValue(
        this.elements.get(EAddressField.MAIN_STREET) as HTMLInputElement,
        "street-address",
      );
    });

    fromEvent(
      <HTMLDivElement>this.elements.get(EAddressField.DELIVERY_CLOSE_BUTTON),
      "click",
    ).subscribe(() =>
      (
        this.elements.get(EAddressField.DELIVERY_SUGGESTIONS) as HTMLDivElement
      ).classList.add("hidden"),
    );

    fromEvent(
      <HTMLDivElement>this.elements.get(EAddressField.BILLING_CLOSE_BUTTON),
      "click",
    ).subscribe(() =>
      (
        this.elements.get(EAddressField.BILLING_SUGGESTIONS) as HTMLDivElement
      ).classList.add("hidden"),
    );

    const mainAddressEventConfig = new AddressAutocompletionEventConfig();
    mainAddressEventConfig.name = "mainAddress";
    mainAddressEventConfig.streetField = this.elements.get(
      EAddressField.MAIN_STREET,
    ) as HTMLInputElement;
    mainAddressEventConfig.countryField = this.elements.get(
      EAddressField.MAIN_COUNTRY,
    ) as HTMLInputElement;
    mainAddressEventConfig.suggestionElement = this.elements.get(
      EAddressField.MAIN_SUGGESTIONS,
    ) as HTMLInputElement;
    mainAddressEventConfig.suggestionListElement = this.elements.get(
      EAddressField.MAIN_SUGGESTION_LIST,
    ) as HTMLInputElement;
    mainAddressEventConfig.postalCodeField = this.elements.get(
      EAddressField.MAIN_POSTAL_CODE,
    ) as HTMLInputElement;
    mainAddressEventConfig.cityField = this.elements.get(
      EAddressField.MAIN_CITY,
    ) as HTMLInputElement;
    mainAddressEventConfig.hintElement = this.elements.get(
      EAddressField.MAIN_HINT,
    ) as HTMLDivElement;

    const deliveryAddressEventConfig = new AddressAutocompletionEventConfig();
    deliveryAddressEventConfig.name = "deliveryAddress";
    deliveryAddressEventConfig.streetField = this.elements.get(
      EAddressField.DELIVERY_STREET,
    ) as HTMLInputElement;
    deliveryAddressEventConfig.countryField = this.elements.get(
      EAddressField.DELIVERY_COUNTRY,
    ) as HTMLInputElement;
    deliveryAddressEventConfig.suggestionElement = this.elements.get(
      EAddressField.DELIVERY_SUGGESTIONS,
    ) as HTMLInputElement;
    deliveryAddressEventConfig.suggestionListElement = this.elements.get(
      EAddressField.DELIVERY_SUGGESTION_LIST,
    ) as HTMLInputElement;
    deliveryAddressEventConfig.postalCodeField = this.elements.get(
      EAddressField.DELIVERY_POSTAL_CODE,
    ) as HTMLInputElement;
    deliveryAddressEventConfig.cityField = this.elements.get(
      EAddressField.DELIVERY_CITY,
    ) as HTMLInputElement;
    deliveryAddressEventConfig.hintElement = this.elements.get(
      EAddressField.DELIVERY_HINT,
    ) as HTMLDivElement;

    const billingAddressEventConfig = new AddressAutocompletionEventConfig();
    billingAddressEventConfig.name = "billingAddress";
    billingAddressEventConfig.streetField = this.elements.get(
      EAddressField.BILLING_STREET,
    ) as HTMLInputElement;
    billingAddressEventConfig.countryField = this.elements.get(
      EAddressField.BILLING_COUNTRY,
    ) as HTMLInputElement;
    billingAddressEventConfig.suggestionElement = this.elements.get(
      EAddressField.BILLING_SUGGESTIONS,
    ) as HTMLInputElement;
    billingAddressEventConfig.suggestionListElement = this.elements.get(
      EAddressField.BILLING_SUGGESTION_LIST,
    ) as HTMLInputElement;
    billingAddressEventConfig.postalCodeField = this.elements.get(
      EAddressField.BILLING_POSTAL_CODE,
    ) as HTMLInputElement;
    billingAddressEventConfig.cityField = this.elements.get(
      EAddressField.BILLING_CITY,
    ) as HTMLInputElement;
    billingAddressEventConfig.hintElement = this.elements.get(
      EAddressField.BILLING_HINT,
    ) as HTMLDivElement;

    const payerAddressEventConfig = new AddressAutocompletionEventConfig();
    payerAddressEventConfig.name = "payerAddress";
    payerAddressEventConfig.streetField = this.elements.get(
      EAddressField.PAYER_STREET,
    ) as HTMLInputElement;
    payerAddressEventConfig.countryField = this.elements.get(
      EAddressField.PAYER_COUNTRY,
    ) as HTMLInputElement;
    payerAddressEventConfig.suggestionElement = this.elements.get(
      EAddressField.PAYER_SUGGESTIONS,
    ) as HTMLInputElement;
    payerAddressEventConfig.suggestionListElement = this.elements.get(
      EAddressField.PAYER_SUGGESTION_LIST,
    ) as HTMLInputElement;
    payerAddressEventConfig.postalCodeField = this.elements.get(
      EAddressField.PAYER_POSTAL_CODE,
    ) as HTMLInputElement;
    payerAddressEventConfig.cityField = this.elements.get(
      EAddressField.PAYER_CITY,
    ) as HTMLInputElement;
    payerAddressEventConfig.hintElement = this.elements.get(
      EAddressField.PAYER_HINT,
    ) as HTMLDivElement;

    // Payer Address

    const eventConfig = [
      mainAddressEventConfig,
      deliveryAddressEventConfig,
      billingAddressEventConfig,
      payerAddressEventConfig,
    ];

    for (const config of eventConfig) {
      this.setInputAutocompleteValue(
        config.streetField,
        this.getBrowserAutoCompletionOffValue(),
      );

      // Complete street based on postal code and city
      fromEvent([config.streetField], "input")
        .pipe(
          distinctUntilChanged(),
          filter(() => config.cityField.value.trim() !== ""),
          debounceTime(200),
          map((event) => (event.target as HTMLInputElement).value),
          switchMap((value) => {
            if (!config?.countryField) {
              console.warn(
                `address-suggestion-validator.ts: No country code for ${config.name} found. Using "de" as fallback.`,
              );
            }

            if (value === "") {
              // stop the api from being called if the string is empty

              config.suggestionListElement.innerText = "";
              config.suggestionElement.classList.add("hidden");

              return [];
            } else {
              const countryValue =
                config?.countryField?.value.toLowerCase() || "de";
              const postalCodeValue = config?.postalCodeField?.value || "";
              const cityValue = config?.cityField?.value || "";

              return AddressAutocompletionRepo.getStreetSuggestionsByPostalCodeAndCity(
                value,
                countryValue,
                postalCodeValue,
                cityValue,
              ).pipe(catchError(() => []));
            }
          }),
          tap((result: AddressAutocompletionResponseModel) =>
            this.makeAddressSuggestions(result, config),
          ),
        )
        .subscribe();
    }
  }

  /*
   * This special method is used to deactivate the input autocomplete from the browser itself completely. In Chrome, it must be 'none', firefox defaults to 'off'.
   * @see https://www.w3docs.com/snippets/html/how-to-disable-browser-autocomplete-and-autofill-on-html-form-and-input-fields.html#:~:text=Add%20autocomplete%3D%22off%22%20onto,input%3E%20element%20of%20the%20form.
   */
  protected getBrowserAutoCompletionOffValue() {
    const agent = navigator.userAgent;

    if (agent.match(/chrome|chromium|crios/i)) {
      //Chrome/Chromium
      return "none";
    } else if (agent.match(/firefox|fxios/i)) {
      // Firefox
      return "off";
    } else {
      return "off";
    }
  }

  protected setInputAutocompleteValue(field: HTMLInputElement, value: string) {
    if (!field) return;

    field.setAttribute("autocomplete", value);
  }

  protected makeAddressSuggestions(
    result: AddressAutocompletionResponseModel,
    config: AddressAutocompletionEventConfig,
  ) {
    if (
      !result ||
      result?.features.length === 0 ||
      result.query.parsed.housenumber
    ) {
      return;
    }

    config.suggestionListElement.innerText = "";

    this.documentClickEvent$
      .pipe(take(1))
      .subscribe(() => config.suggestionElement.classList.add("hidden"));

    const queryTextMadeByUser = result.query.text;

    for (const feature of result.features) {
      const properties = feature.properties;
      const suggestion = document.createElement("div");

      if (
        this.isSameCity(
          result.query?.parsed?.city?.toLowerCase().trim(),
          properties.city?.toLowerCase().trim(),
        ) &&
        !this.isSameStreet(
          config.streetField.value.toLowerCase().trim(),
          properties.street?.toLowerCase().trim(),
        )
      ) {
        const text = `${properties.street}`;
        suggestion.innerHTML = text.replace(
          queryTextMadeByUser,
          DomService.removeScriptsFromString(
            `<strong>${queryTextMadeByUser}</strong>`,
          ),
        );

        config.suggestionListElement.append(suggestion);

        fromEvent(suggestion, "click")
          .pipe(take(1))
          .subscribe(() => this.addSuggestions(feature, config));
      }
    }

    // zeige kein leeres Popup an
    if (config.suggestionListElement.childNodes.length > 0)
      config.suggestionElement.classList.remove("hidden");
  }

  protected isSameCity(requestedCity: string, resultCity: string): boolean {
    return requestedCity === resultCity;
  }

  protected isSameStreet(
    streetFieldValue: string,
    resultStreetValue: string,
  ): boolean {
    return streetFieldValue === resultStreetValue;
  }

  protected addSuggestions(
    feature: AddressAutocompletionFeatureResultModel,
    config: AddressAutocompletionEventConfig,
  ) {
    config.streetField.value = feature.properties.street;
    config.suggestionListElement.innerText = "";
    config.suggestionElement.classList.add("hidden");

    this.setInputAutocompleteValue(config.streetField, "street-address");
    config.streetField.focus();
  }

  protected buildElementList(): void {
    const list = new Map();

    // Main Address
    list.set(
      EAddressField.MAIN_ADDRESS_FIELD,
      this.formElement.querySelector<HTMLInputElement>(
        ".address-editable-mainAddress",
      ),
    );

    if (!list.get(EAddressField.MAIN_ADDRESS_FIELD)) return;

    // Main Address Fields
    list.set(
      EAddressField.MAIN_STREET,
      list
        .get(EAddressField.MAIN_ADDRESS_FIELD)
        .querySelector("input[data-street]"),
    );
    list.set(
      EAddressField.MAIN_POSTAL_CODE,
      list
        .get(EAddressField.MAIN_ADDRESS_FIELD)
        .querySelector("input[data-postalcode]"),
    );
    list.set(
      EAddressField.MAIN_CITY,
      list
        .get(EAddressField.MAIN_ADDRESS_FIELD)
        .querySelector("input[data-city]"),
    );
    list.set(
      EAddressField.MAIN_SUGGESTIONS,
      list
        .get(EAddressField.MAIN_ADDRESS_FIELD)
        .querySelector(".street-suggestions"),
    );
    list.set(
      EAddressField.MAIN_HINT,
      list
        .get(EAddressField.MAIN_ADDRESS_FIELD)
        .querySelector("div[data-hint]"),
    );
    list.set(
      EAddressField.MAIN_SUGGESTION_LIST,
      list
        .get(EAddressField.MAIN_ADDRESS_FIELD)
        .querySelector(".street-suggestion-list"),
    );
    list.set(
      EAddressField.MAIN_CLOSE_BUTTON,
      list
        .get(EAddressField.MAIN_ADDRESS_FIELD)
        .querySelector(".suggestion-close-button"),
    );
    list.set(
      EAddressField.MAIN_COUNTRY,
      this.formElement.querySelector("#input-field-mainAddress-countryCode"),
    );

    // Delivery Address
    list.set(
      EAddressField.DELIVERY_ADDRESS_FIELD,
      this.formElement.querySelector<HTMLInputElement>(
        ".address-editable-deliveryAddress",
      ),
    );

    if (!list.get(EAddressField.DELIVERY_ADDRESS_FIELD)) return;

    // Delivery Address Fields
    list.set(
      EAddressField.DELIVERY_STREET,
      list
        .get(EAddressField.DELIVERY_ADDRESS_FIELD)
        .querySelector("input[data-street]"),
    );
    list.set(
      EAddressField.DELIVERY_POSTAL_CODE,
      list
        .get(EAddressField.DELIVERY_ADDRESS_FIELD)
        .querySelector("input[data-postalcode]"),
    );
    list.set(
      EAddressField.DELIVERY_CITY,
      list
        .get(EAddressField.DELIVERY_ADDRESS_FIELD)
        .querySelector("input[data-city]"),
    );
    list.set(
      EAddressField.DELIVERY_SUGGESTIONS,
      list
        .get(EAddressField.DELIVERY_ADDRESS_FIELD)
        .querySelector(".street-suggestions"),
    );
    list.set(
      EAddressField.DELIVERY_HINT,
      list
        .get(EAddressField.DELIVERY_ADDRESS_FIELD)
        .querySelector("div[data-hint]"),
    );
    list.set(
      EAddressField.DELIVERY_SUGGESTION_LIST,
      list
        .get(EAddressField.DELIVERY_ADDRESS_FIELD)
        .querySelector(".street-suggestion-list"),
    );
    list.set(
      EAddressField.DELIVERY_CLOSE_BUTTON,
      list
        .get(EAddressField.DELIVERY_ADDRESS_FIELD)
        .querySelector(".suggestion-close-button"),
    );
    list.set(
      EAddressField.DELIVERY_COUNTRY,
      this.formElement.querySelector(
        "#input-field-deliveryAddress-countryCode",
      ),
    );

    // Billing Address
    list.set(
      EAddressField.BILLING_ADDRESS_FIELD,
      this.formElement.querySelector<HTMLInputElement>(
        ".address-editable-billingAddress",
      ),
    );

    if (!list.get(EAddressField.BILLING_ADDRESS_FIELD)) return;

    // Billing Address Fields
    list.set(
      EAddressField.BILLING_STREET,
      list
        .get(EAddressField.BILLING_ADDRESS_FIELD)
        .querySelector("input[data-street]"),
    );
    list.set(
      EAddressField.BILLING_POSTAL_CODE,
      list
        .get(EAddressField.BILLING_ADDRESS_FIELD)
        .querySelector("input[data-postalcode]"),
    );
    list.set(
      EAddressField.BILLING_CITY,
      list
        .get(EAddressField.BILLING_ADDRESS_FIELD)
        .querySelector("input[data-city]"),
    );
    list.set(
      EAddressField.BILLING_SUGGESTIONS,
      list
        .get(EAddressField.BILLING_ADDRESS_FIELD)
        .querySelector(".street-suggestions"),
    );
    list.set(
      EAddressField.BILLING_HINT,
      list
        .get(EAddressField.BILLING_ADDRESS_FIELD)
        .querySelector("div[data-hint]"),
    );
    list.set(
      EAddressField.BILLING_SUGGESTION_LIST,
      list
        .get(EAddressField.BILLING_ADDRESS_FIELD)
        .querySelector(".street-suggestion-list"),
    );
    list.set(
      EAddressField.BILLING_CLOSE_BUTTON,
      list
        .get(EAddressField.BILLING_ADDRESS_FIELD)
        .querySelector(".suggestion-close-button"),
    );
    list.set(
      EAddressField.BILLING_COUNTRY,
      this.formElement.querySelector("#input-field-billingAddress-countryCode"),
    );

    // Payer Address
    list.set(
      EAddressField.PAYER_ADDRESS_FIELD,
      this.formElement.querySelector<HTMLInputElement>(
        ".address-editable-payerAddress",
      ),
    );

    if (!list.get(EAddressField.PAYER_ADDRESS_FIELD)) return;

    // Payer Address Fields
    list.set(
      EAddressField.PAYER_STREET,
      list
        .get(EAddressField.PAYER_ADDRESS_FIELD)
        .querySelector("input[data-street]"),
    );
    list.set(
      EAddressField.PAYER_POSTAL_CODE,
      list
        .get(EAddressField.PAYER_ADDRESS_FIELD)
        .querySelector("input[data-postalcode]"),
    );
    list.set(
      EAddressField.PAYER_CITY,
      list
        .get(EAddressField.PAYER_ADDRESS_FIELD)
        .querySelector("input[data-city]"),
    );
    list.set(
      EAddressField.PAYER_SUGGESTIONS,
      list
        .get(EAddressField.PAYER_ADDRESS_FIELD)
        .querySelector(".street-suggestions"),
    );
    list.set(
      EAddressField.PAYER_HINT,
      list
        .get(EAddressField.PAYER_ADDRESS_FIELD)
        .querySelector("div[data-hint]"),
    );
    list.set(
      EAddressField.PAYER_SUGGESTION_LIST,
      list
        .get(EAddressField.PAYER_ADDRESS_FIELD)
        .querySelector(".street-suggestion-list"),
    );
    list.set(
      EAddressField.PAYER_CLOSE_BUTTON,
      list
        .get(EAddressField.PAYER_ADDRESS_FIELD)
        .querySelector(".suggestion-close-button"),
    );
    list.set(
      EAddressField.PAYER_COUNTRY,
      this.formElement.querySelector("#input-field-payerAddress-countryCode"),
    );

    this.elements = list;
  }
}
